import {
  Box,
  Button,
  Flex,
  Icon,
  Image,
  Text,
  Skeleton,
  SkeletonCircle,
  useDisclosure,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from '@chakra-ui/react'
import { FaArrowLeft } from 'react-icons/fa'
import { PiWarningCircleBold } from 'react-icons/pi'
import { IoEyeOutline } from 'react-icons/io5'
import { useUser } from '@/src/store/user-store'
import { PenIcon, Share2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { SubscribeBtn } from '@/components/video-page/SubscribeBtn'
import { usePathname } from 'next/navigation'
import { useChannelDetailsQuery } from '@/src/queries/my-channel/channel-details'
import { useChannel } from '@/src/store/my-channel-store'
import { formatSubscriberCount } from '@/components/video-page/utils/formatOptions'
import { getInitials } from '../avatar'
import ShareChannel from './share-channel'
import EditChannel from './editchannel'
import { EditIcon, HamburgerIcon } from '@chakra-ui/icons'
import ReportModal from './report-modal'

type ChannelHeaderProps = {
  isCreator: boolean
  isPublic: boolean
  allowEdit: boolean
  subscribers: string
  videos: string
  themeColor?: string
  avatarUrl: string
  headerImageUrl: string
  creatorName: string
  isSubscribed?: boolean
  channelId?: string
  channelShareName: string
  isVerifiedTick: string
  editOpen?: () => void
}

const PublicBanner: React.FC<Pick<ChannelHeaderProps, 'isCreator'>> = ({
  isCreator,
}) => {
  const { isPublic, setIsPublic } = useChannel()
  return (
    isCreator &&
    isPublic && (
      <Flex
        my={4}
        alignItems="center"
        justifyContent="space-between"
        px={2}
        py={2}
        rounded="md"
        bg="lyk-green"
      >
        <Box w={['auto', 'auto', 'auto', '180px']}>
          <Button
            leftIcon={<Icon as={FaArrowLeft} h={[3, 3, 4]} w={[3, 3, 4]} />}
            colorScheme="gray"
            size={['sm', 'sm', 'md']}
            onClick={() => setIsPublic(false)}
          >
            Back to Admin
          </Button>
        </Box>

        <Flex
          alignItems="center"
          gap={2}
          display={['none', 'none', 'none', 'flex']}
        >
          <Icon as={PiWarningCircleBold} h={6} w={6} color="white" />
          <Text color="white" justifySelf={'center'}>
            You are currently viewing this channel in Public View mode.
          </Text>
        </Flex>

        <Box w="180px" display={['none', 'none', 'none', 'block']}></Box>

        <Flex
          alignItems="center"
          gap={2}
          display={['flex', 'flex', 'flex', 'none']}
        >
          <Icon as={PiWarningCircleBold} h={4} w={4} color="white" />
          <Text color="white" justifySelf={'center'} fontSize="sm">
            Public View mode
          </Text>
        </Flex>
      </Flex>
    )
  )
}

const HeaderImage: React.FC<Pick<ChannelHeaderProps, 'headerImageUrl'>> = ({
  headerImageUrl,
}) => {
  return (
    <Box
      position="relative"
      h={['200px', '200px', '300px']}
      w="100%"
      bg="gray.200"
      rounded="2xl"
      overflow="hidden"
      className="group"
    >
      <Box
        position="relative"
        h={['200px', '200px', '300px']}
        w="100%"
        cursor="pointer"
        display="flex"
        className="group"
        zIndex={10}
      >
        <Image
          fallbackSrc={'/my-channel/fallback.png'}
          src={getImageUrl(headerImageUrl)}
          h="100%"
          w="100%"
          objectFit="cover"
          position="relative"
          zIndex={100}
          alt=""
        />
      </Box>
    </Box>
  )
}

const getImageUrl = (url: string) => {
  if (url.startsWith('http')) {
    return url
  } else {
    return `https://cdn.lykstage.com/img/${url}`
  }
}

const ChannelAv: React.FC<
  Pick<
    ChannelHeaderProps,
    'avatarUrl' | 'creatorName' | 'themeColor' | 'allowEdit' | 'editOpen'
  >
> = ({ avatarUrl, creatorName, themeColor, allowEdit, editOpen }) => {
  return (
    <Box
      position="relative"
      h={['100px', '100px', '150px']}
      w={['100px', '100px', '150px']}
      display="flex"
      className="group"
      rounded="full"
    >
      {!allowEdit && (
        <Flex
          position="absolute"
          bottom={1}
          right={-3}
          zIndex={1000}
          alignItems="center"
          justifyContent="center"
        >
          <Flex
            cursor={'pointer'}
            alignItems="center"
            justifyContent="center"
            w={['35px', '35px', '45px']}
            h={['35px', '35px', '45px']}
            bg="lyk-green"
            opacity="0.9"
            rounded="full"
            onClick={() => {
              editOpen && editOpen()
            }}
          >
            <Icon
              as={EditIcon}
              h={['18px', '18px', '22px']}
              w={['18px', '18px', '22px']}
              color="white"
            />
          </Flex>
        </Flex>
      )}
      <Box
        position="relative"
        h={['100px', '100px', '150px']}
        w={['100px', '100px', '150px']}
        bg={themeColor}
        rounded="full"
        overflow="hidden"
        pos="relative"
      >
        <Image
          src={getImageUrl(avatarUrl)}
          h="100%"
          w="100%"
          fallback={
            <Text
              className="flex h-full w-full items-center justify-center rounded-full text-center text-4xl font-medium tracking-wider text-[#2A2A2A] sm:text-5xl md:text-6xl"
              bg={themeColor || 'lyk-green'}
              color={'white'}
            >
              {getInitials(creatorName || '')}
            </Text>
          }
          objectPosition="center"
          objectFit="cover"
          position="relative"
          zIndex={99}
          alt=""
        />
      </Box>
    </Box>
  )
}

const ChannelInfo: React.FC<
  Omit<ChannelHeaderProps, 'headerImageUrl' | 'isPublic'>
> = ({
  isCreator,
  subscribers,
  videos,
  avatarUrl,
  creatorName,
  allowEdit,
  themeColor,
  isSubscribed,
  channelId,
  channelShareName,
  isVerifiedTick,
}) => {
  const { isPending } = useChannelDetailsQuery(channelShareName)
  const { user } = useUser()

  const {
    isOpen: shareIsOpen,
    onOpen: shareOnOpen,
    onClose: shareOnClose,
  } = useDisclosure()

  const {
    isOpen: editIsOpen,
    onOpen: editOnOpen,
    onClose: editOnClose,
  } = useDisclosure()

  const { isPublic, setIsPublic } = useChannel()

  const pathname = usePathname()
  const [isAccountPage, setIsAccountPage] = useState<boolean>(false)

  const {
    isOpen: reportIsOpen,
    onOpen: reportOnOpen,
    onClose: reportOnClose,
  } = useDisclosure()

  useEffect(() => {
    if (pathname == '/account') {
      setIsAccountPage(true)
    } else {
      setIsAccountPage(false)
    }
  }, [pathname, setIsAccountPage])

  return (
    <Flex
      my={4}
      alignItems={['stretch', 'stretch', 'center']}
      justifyContent={['center', 'stretch', 'space-between']}
      direction={['column', 'column', 'row']}
      columnGap={4}
      rowGap={6}
      flexWrap={'wrap'}
    >
      <EditChannel
        name={creatorName}
        themeColor={themeColor || ''}
        imageUrl={avatarUrl}
        onClose={editOnClose}
        isOpen={editIsOpen}
        channelShareName={channelShareName}
        accountFlow={!allowEdit}
      />
      <ShareChannel
        name={creatorName}
        link={`https://lykstage.com/c/${channelShareName || ''}`}
        themeColor={themeColor || ''}
        imageUrl={avatarUrl}
        onClose={shareOnClose}
        isOpen={shareIsOpen}
      />
      <ReportModal
        onClose={reportOnClose}
        isOpen={reportIsOpen}
        channelId={channelId || ''}
      />
      <Flex alignItems={'center'} gap={5}>
        {isPending ? (
          <SkeletonCircle
            h={['100px', '100px', '150px']}
            w={['100px', '100px', '150px']}
          ></SkeletonCircle>
        ) : (
          <ChannelAv
            avatarUrl={avatarUrl}
            creatorName={creatorName}
            themeColor={themeColor}
            allowEdit={allowEdit}
            editOpen={editOnOpen}
          />
        )}

        <Flex direction="column" gap={[1, 1, 3]}>
          {isPending ? (
            <Skeleton fontSize={['lg', 'lg', '2xl']}>
              <h1>Channel Name</h1>
            </Skeleton>
          ) : (
            <>
              <Text fontSize={['lg', 'lg', '2xl']} fontWeight="semibold">
                <Flex align={'center'}>
                  {creatorName.length > 50 ? `${creatorName.substring(0, 50)}...` : creatorName}&nbsp;&nbsp;{' '}
                  <Image
                  src="/my-channel/verified-tag.svg"
                  alt="verified-tag"
                  display={isVerifiedTick == '1' ? 'inline' : 'none'}
                  h="auto"
                  w={[6, 6, 7]}
                  mb={1}
                />
                 {!isCreator &&<Menu>
                    <MenuButton
                      as={IconButton}
                      aria-label="Options"
                      _hover={{ bg: 'transparent !important' }}
                      _active={{ bg: 'transparent !important' }}
                      icon={
                        <Image
                          src="/my-channel/menu-vertical.svg"
                          alt="dots"
                          width={5}
                          height={5}
                          cursor={'pointer'}
                        />
                      }
                      variant="outline"
                    />
                    <MenuList
                      shadow={'lg'}
                      sx={{
                        border: '2px solid #ECECEC',
                        borderRadius: '4px',
                      }}
                      zIndex={999}
                      px={2}
                    >
                      <MenuItem
                        width={'fit-content'}
                        icon={
                          <Image
                            src={'/rewards/exclamatoryMark2.svg'}
                            alt="Icon"
                            w={4}
                            h={4}
                          />
                        }
                        fontSize={14}
                        color={'#3E3E3E99'}
                        _hover={{ bg: 'transparent !important' }}
                        _active={{ bg: 'transparent !important' }}
                        _focus={{ bg: 'transparent !important' }}
                        py={0}
                        px={0}
                        onClick={reportOnOpen}
                      >
                        Report user
                      </MenuItem>
                    </MenuList>
                  </Menu>}
                </Flex>
              </Text>
              <Flex alignItems="center" fontSize={['md', 'md', 'lg']} gap={2}>
                <Text>{formatSubscriberCount(subscribers)}</Text>
                <Text fontSize="xs" fontWeight="extrabold" color="gray.400">
                  &#9679;
                </Text>
                <Text>
                  {videos !== null &&
                    parseInt(videos) +
                      (parseInt(videos) > 1 || parseInt(videos) == 0
                        ? ' videos'
                        : ' video')}
                </Text>
              </Flex>
            </>
          )}

          <Flex gap={'0.5em'} mt={2} flexWrap="wrap">
            {isPublic && (
              <Box>
                <SubscribeBtn
                  isSubscribed={isSubscribed || false}
                  eventId={''}
                  channelId={channelId || ''}
                  invalidateKey="channel-details"
                  isDisabled={
                    user?.channelShareName.toLowerCase() ==
                    channelShareName.toLowerCase()
                  }
                />
              </Box>
            )}

            {isPending ? (
              <Skeleton fontSize={['lg', 'lg', '2xl']}>
                <Button rounded={'full'} leftIcon={<Share2 width={'1em'} />}>
                  Share Channel
                </Button>
              </Skeleton>
            ) : (
              <>
                {!isAccountPage ? (
                  <>
                    <Button
                      rounded={'full'}
                      leftIcon={<Share2 width={'1em'} />}
                      _hover={{ opacity: 0.7 }}
                      onClick={shareOnOpen}
                      fontSize={['md', 'sm', 'md']}
                      hideBelow={'md'}
                    >
                      Share Channel
                    </Button>
                    <IconButton
                      rounded={'full'}
                      aria-label="Share Channel"
                      icon={<Share2 width={'1em'} />}
                      _hover={{ opacity: 0.7 }}
                      onClick={shareOnOpen}
                      fontSize={['md', 'sm', 'md']}
                      hideFrom={'md'}
                    />
                  </>
                ) : null}
              </>
            )}
          </Flex>
        </Flex>
      </Flex>

      {isCreator && !isPublic && allowEdit && (
        <Flex alignItems="center" gap={2}>
          <Button
            leftIcon={<Icon as={PenIcon} h={4} w={4} />}
            size={['md', 'sm', 'md']}
            flex={[1, 1, '0 1 auto']}
            onClick={editOnOpen}
          >
            Edit Channel
          </Button>
          <Button
            leftIcon={<Icon as={IoEyeOutline} h={4} w={4} />}
            size={['md', 'sm', 'md']}
            flex={[1, 1, '0 1 auto']}
            onClick={() => setIsPublic(true)}
          >
            Public View
          </Button>
        </Flex>
      )}
    </Flex>
  )
}

export default function ChannelHeader({
  isCreator,
  subscribers,
  videos,
  avatarUrl,
  headerImageUrl,
  creatorName,
  themeColor,
  isSubscribed,
  channelId,
  channelShareName,
  allowEdit,
  isVerifiedTick,
}: Omit<ChannelHeaderProps, 'isPublic'>) {
  const { setIsPublic } = useChannel()

  const { user } = useUser()
  const pathname = usePathname()

  useEffect(() => {
    if (
      channelShareName.toLowerCase() == user?.channelShareName.toLowerCase() ||
      pathname == '/account'
    ) {
      setIsPublic(false)
    } else {
      setIsPublic(true)
    }
  }, [pathname, setIsPublic, user?.channelShareName, channelShareName])

  return (
    <Box my={4}>
      <PublicBanner isCreator={isCreator} />

      <HeaderImage headerImageUrl={headerImageUrl} />

      <ChannelInfo
        allowEdit={allowEdit}
        isCreator={isCreator}
        subscribers={subscribers}
        videos={videos}
        themeColor={themeColor}
        avatarUrl={avatarUrl}
        creatorName={creatorName}
        isSubscribed={isSubscribed}
        channelId={channelId}
        channelShareName={channelShareName}
        isVerifiedTick={isVerifiedTick}
      />
    </Box>
  )
}
